/* jshint esversion: 6, -W097 */
/* globals module */
'use strict';

const constants = require('./constants');

/**
 * @module enervee_utils.ads
 */
NativeAdsService.$inject = [
    '$q',
    'AnalyticsService',
    'GetFetchService',
    'StorageService',
    'UtilService',
];
function NativeAdsService(
    $q,
    AnalyticsService,
    GetFetchService,
    StorageService,
    UtilService
) {
    const NATIVE_ADS_STORAGE_PATH = '_native-ads';
    const NATIVE_ADS_URL = UtilService.getSiteSetting('cms_base_url') + 'native-ads/';
    return {
        isEnabled: _isEnabled(),
        getAd: getAd,
        trackAdInteraction: trackAdInteraction,
    };

    /**
     * Resolves with the appropriate ad for a tagname + cateogryId, or an empty object if no
     * matching ad was found.
     *
     * @param {string} tagname
     * @param {string|Number} categoryId
     *
     * @return {Promise<Object>}
     */
    function getAd(tagname, categoryId) {
        let storedAd = StorageService.get(
            [NATIVE_ADS_STORAGE_PATH, tagname, categoryId],
            {defaultValue: null}
        );
        if (storedAd !== null) return $q.resolve(storedAd);
        return _fetchNativeAds().then(ads => {
            let resultingAd = _filterAdByTagnameAndCategoryId(ads, tagname, categoryId);
            StorageService.set([NATIVE_ADS_STORAGE_PATH, tagname, categoryId], resultingAd);
            return resultingAd;
        });
    }

    /**
     * Fires a Segment event for when an ad is interacted with, whether viewed, clicked, or
     * any other interaction.
     * 
     * @param {string} event The name of the event
     * @param {string} action The desired action being tracked
     * @param {string} tagname The specific tagname of the ad slot being tracked
     * @param {Object} ad The ad config object for the ad slot being interacted with
     * @param {string|Number} [categoryId] The category id provided to the ad slot, if any
     * 
     * @return {Promise} Resolves once the tracking event is complete
     */
    function trackAdInteraction(event, action, tagname, ad, categoryId) {
        return AnalyticsService.reportEvent(event, {
            action,
            ad: Object.assign({ slot_tagname: tagname }, ad),
            category_id: categoryId || null,
        });
    }

    /**
     * Given an array of ads, it selects the proper ad (if any), given the tagname and cateogryId.
     * Priority is given to:
     * 1) Matching categoryId
     * 2) Tagname (if categoryId is null, or none match)
     *
     * @param {Array<Object>} ads An array of ads retrieved from the CMS
     * @param {Array<string>} ads[].tagnames_list
     * @param {Number|null} ads[].category_id
     * @param {string} tagname
     * @param {string|Number} categoryId
     *
     * @return {Object} The appropriate ad, or an empty object.
     */
    function _filterAdByTagnameAndCategoryId(ads, tagname, categoryId) {
        let _categoryId = categoryId ? parseInt(categoryId) : null;
        let filteredAds = ads.filter(ad => ad.tagnames_list.indexOf(tagname) > -1);
        if (_categoryId !== null) {
            let fallbackAd = filteredAds.find(ad => ad.category_id === null);
            let categorySpecificAd = filteredAds.find(ad => ad.category_id === _categoryId);
            return categorySpecificAd || fallbackAd || {};
        }
        return filteredAds[0] || {};
    }

    /**
     * Fetches and stores the native ads from the CMS.
     *
     * @return {Promise<Array<Object>>}
     */
    function _fetchNativeAds() {
        return GetFetchService.get('native-ads', NATIVE_ADS_URL);
    }

    /**
     * Shorthand for when this feature should be enabled.
     * Boils down to:
     * - Native Ads are enabled
     * - AppNexus Tags are _not_ enabled
     * - Google Ads are _not_ enabled
     * @returns {Boolean}
     */
    function _isEnabled() {
        return (
            UtilService.isFeatureEnabled(constants.NATIVE_ADS_FEATURE) &&
            !UtilService.isFeatureEnabled(constants.APPNEXUS_FEATURE) &&
            !UtilService.isFeatureEnabled(constants.GOOGLE_ADS_FEATURE)
        );
    }
}

module.exports = NativeAdsService;
